
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Role } from '@shared/user';
import { RolesSchema } from '@shared/user';
import { Badge, Button, ROLE_BADGE_TONE, Row, Spacer, Stack, Text, Time } from '@client/shared/design';
import { messageFor } from '@client/shared/lib/error-message.ts';
import { initials } from '@client/shared/lib/initials.ts';
import {
  updateUserRoles,
  setUserActive,
  GROUP_USERS_QUERY_KEY,
  type GroupUser,
} from './user-operations.ts';
import { RolePicker } from './RolePicker.tsx';
import styles from './Users.module.css';

export interface UserRowProps {
  user: GroupUser;
  isSelf: boolean;
}

export function UserRow({ user, isSelf }: UserRowProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draftRoles, setDraftRoles] = useState<Role[]>([...user.roles]);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: GROUP_USERS_QUERY_KEY });

  const saveRoles = useMutation({
    mutationFn: updateUserRoles,
    onSuccess: async () => {
      await invalidate();
      setEditing(false);
    },
    onError: (e) => setError(messageFor(e, 'Something went wrong. Try again.')),
  });

  const toggleActive = useMutation({
    mutationFn: setUserActive,
    onSuccess: async () => {
      setConfirmingDeactivate(false);
      await invalidate();
    },
    onError: (e) => {
      setConfirmingDeactivate(false);
      setError(messageFor(e, 'Something went wrong. Try again.'));
    },
  });

  function beginEdit() {
    setError(null);
    setDraftRoles([...user.roles]);
    setEditing(true);
  }

  function cancelEdit() {
    setError(null);
    setEditing(false);
  }

  function commitRoles() {
    setError(null);
    const parsed = RolesSchema.safeParse(draftRoles);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Pick at least one role.');
      return;
    }
    saveRoles.mutate({ userId: user.id, roles: parsed.data });
  }

  const sortedRoles = [...user.roles].sort();
  const deactivated = !user.isActive;

  return (
    <li className={styles.row} data-inactive={deactivated || undefined}>
      <Stack gap={editing ? 4 : 3}>
        <Row gap={3} align="center" wrap>
          <span className={styles.avatar} aria-hidden="true">
            {initials(user.name)}
          </span>

          <Stack gap={0} className={styles.identity}>
            <Row gap={2} align="baseline" wrap>
              <Text weight="semibold" as="span" className={styles.name}>
                {user.name}
              </Text>
              <StatusBadge user={user} />
            </Row>
            <Text size="sm" tone="muted" as="span" className={styles.email}>
              {user.email}
            </Text>
          </Stack>

          <Spacer />

          {!editing && (
            <Row gap={2} align="center" wrap className={styles.rolesCol}>
              {sortedRoles.map((role) => (
                <Badge key={role} tone={ROLE_BADGE_TONE[role]} dot>
                  {role}
                </Badge>
              ))}
            </Row>
          )}

          {!editing && (
            <Row gap={1} align="center" className={styles.actions}>
              <Button variant="ghost" size="sm" onClick={beginEdit}>
                Edit roles
              </Button>
              {deactivated ? (
                <Button
                  variant="secondary"
                  size="sm"
                  loading={toggleActive.isPending}
                  onClick={() => toggleActive.mutate({ userId: user.id, active: true })}
                >
                  Reactivate
                </Button>
              ) : confirmingDeactivate ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={toggleActive.isPending}
                    onClick={() => setConfirmingDeactivate(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    tone="danger"
                    loading={toggleActive.isPending}
                    onClick={() => toggleActive.mutate({ userId: user.id, active: false })}
                  >
                    Deactivate
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  tone="danger"
                  disabled={isSelf}
                  title={isSelf ? "You can’t deactivate your own account." : undefined}
                  onClick={() => setConfirmingDeactivate(true)}
                >
                  Deactivate
                </Button>
              )}
            </Row>
          )}
        </Row>

        {editing && (
          <Stack gap={3} className={styles.editPanel}>
            <RolePicker
              name={`roles-${user.id}`}
              value={draftRoles}
              onChange={setDraftRoles}
              disabled={saveRoles.isPending}
            />
            {error && (
              <Text size="sm" tone="danger" role="alert">
                {error}
              </Text>
            )}
            <Row gap={2} justify="end">
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saveRoles.isPending}>
                Cancel
              </Button>
              <Button size="sm" loading={saveRoles.isPending} onClick={commitRoles}>
                Save roles
              </Button>
            </Row>
          </Stack>
        )}

        {!editing && error && (
          <Text size="sm" tone="danger" role="alert">
            {error}
          </Text>
        )}

        {!editing && (
          <Text size="xs" tone="muted" as="span" className={styles.meta}>
            {user.pending ? 'Invited' : 'Joined'} <Time at={user.createdAt} date />
          </Text>
        )}
      </Stack>
    </li>
  );
}

function StatusBadge({ user }: { user: GroupUser }) {
  if (!user.isActive) {
    return (
      <Badge tone="danger" dot>
        Deactivated
      </Badge>
    );
  }
  if (user.pending) {
    return (
      <Badge tone="warn" dot>
        Pending
      </Badge>
    );
  }
  return (
    <Badge tone="ok" dot>
      Active
    </Badge>
  );
}
